import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  // Tracks whether the drawer is meant to be open. A section morph can drop the
  // native dialog's top-layer state without firing a close, so this intent flag
  // is the source of truth for re-asserting "open" after a cart update.
  #shouldStayOpen = false;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  #handleDialogOpen = () => {
    this.#shouldStayOpen = true;
    this.#updateStickyState();
  };

  // Only fires on an intentional close (close button, backdrop, Escape), never
  // on a section morph, so the intent flag survives cart updates.
  #handleDialogClose = () => {
    this.#shouldStayOpen = false;
  };

  // `CartAddEvent` and `CartUpdateEvent` share the `cart:update` name, so this
  // runs for adds, quantity changes, and line removals alike.
  #handleCartAdd = () => {
    // cart:update listeners run synchronously. Another listener morphs the
    // header section (which contains the drawer) after this handler runs, and
    // that morph can drop the native dialog's top-layer state. Re-check after
    // all listeners and synchronous morphs have completed.
    queueMicrotask(() => {
      if (!this.isConnected) return;

      // The morph may have replaced the dialog node. Component refs are only
      // refreshed in a later microtask (via updatedCallback / MutationObserver),
      // so without this the first edit would re-open a stale, detached dialog
      // still showing the pre-morph cart. Refresh refs to the live DOM first.
      this.updatedCallback();

      const { dialog } = this.refs;

      // Keep an already-open drawer open across quantity/line edits. This is
      // independent of `auto-open`, which only governs opening on add.
      if (this.#shouldStayOpen) {
        if (dialog && !dialog.open) this.showDialog();
        return;
      }

      // Open the drawer on add only when the merchant enabled auto-open.
      if (this.hasAttribute('auto-open')) this.showDialog();
    });
  };

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
